// src/features/wiki/Wiki.logic.tsx

import { collection, doc, DocumentReference, getDocs, query, where, writeBatch } from 'firebase/firestore';
import { createItemWithId, fetchItemsWithFilter, Filter, omitUndefinedDeep, updateItem } from '../../api/generic';
import { db } from '../../config/firebase';
import { WikiPage, WikiPageCreate, WikiPageUpdate } from '../../models/Wiki';

export interface WikiTreeNode extends WikiPage {
  children: WikiTreeNode[];
}

export function wikiCollectionPath(tenantId: string) {
  return `${tenantId}_wiki`;
}

// -------------------------
// 🔧 Hierarchy helpers
// -------------------------
export function deriveHierarchy(slug: string): { parentSlug?: string; level: number } {
  const parts = slug.split('-');
  return parts.length === 1
    ? { parentSlug: undefined, level: 1 }
    : { parentSlug: parts.slice(0, -1).join('-'), level: parts.length };
}

export function isSubpage(parentSlug: string, slug: string) {
  return slug.startsWith(parentSlug + '-');
}

export function buildWikiTree(pages: WikiPage[]): WikiTreeNode[] {
  const byParent = new Map<string | undefined, WikiPage[]>();
  for (const page of pages) {
    const key = page.parentSlug;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(page);
  }

  function recurse(parent?: string): WikiTreeNode[] {
    return (byParent.get(parent) || []).map((p) => ({
      ...p,
      children: recurse(p.slug),
    }));
  }

  return recurse(undefined);
}

// -------------------------
// 🔐 Permission helpers
// -------------------------
export function canEdit(page: WikiPage, role: string, uid: string) {
  return role === 'god' || role === 'admin' || (role === 'user' && page.createdBy === uid);
}

export function canPublish(role: string) {
  return role === 'god' || role === 'admin';
}

// -------------------------
// 🧰 Filters
// -------------------------
type F = Filter<WikiPage>;

export const slugEquals = (slug: string): F => ({
  field: 'slug',
  operator: '==',
  value: slug,
});

export const statusEquals = (status: WikiPage['status']): F => ({
  field: 'status',
  operator: '==',
  value: status,
});

export const visibilityIn = (vis: WikiPage['visibility'][]): F => ({
  field: 'visibility',
  operator: 'in',
  value: vis,
});

export const parentSlugEquals = (parentSlug: string): F => ({
  field: 'parentSlug',
  operator: '==',
  value: parentSlug,
});

export const levelEquals = (level: number): F => ({
  field: 'level',
  operator: '==',
  value: level,
});

export const categoryContains = (category: string): F => ({
  field: 'categories',
  operator: 'array-contains',
  value: category,
});

export const tagContains = (tag: string): F => ({
  field: 'tags',
  operator: 'array-contains',
  value: tag,
});

export const createdByEquals = (uid: string): F => ({
  field: 'createdBy',
  operator: '==',
  value: uid,
});

export const pinnedIs = (pinned = true): F => ({
  field: 'pinned',
  operator: '==',
  value: pinned,
});

export const updatedAtGte = (ts: number): F => ({
  field: 'updatedAt',
  operator: '>=',
  value: ts,
});

export const updatedAtLte = (ts: number): F => ({
  field: 'updatedAt',
  operator: '<=',
  value: ts,
});

// -------------------------
// 🔍 Queries
// -------------------------
export async function getWikiPage(tenantId: string, slug: string): Promise<WikiPage | undefined> {
  const pages = await fetchItemsWithFilter<WikiPage>(`${tenantId}_wiki`, [slugEquals(slug)]);
  return pages[0];
}

export async function listTopLevelPages(tenantId: string) {
  return fetchItemsWithFilter<WikiPage>(`${tenantId}_wiki`, [levelEquals(1)]);
}

export async function listSubpages(tenantId: string, parent: string) {
  return fetchItemsWithFilter<WikiPage>(`${tenantId}_wiki`, [parentSlugEquals(parent)]);
}

export async function listPinned(tenantId: string) {
  return fetchItemsWithFilter<WikiPage>(`${tenantId}_wiki`, [pinnedIs(true)]);
}

export async function listVisiblePublished(tenantId: string, tenantView = true) {
  const vis: WikiPage['visibility'][] = tenantView ? ['public', 'tenant'] : ['public'];
  return fetchItemsWithFilter<WikiPage>(`${tenantId}_wiki`, [statusEquals('published'), visibilityIn(vis)]);
}

export async function listMyDrafts(tenantId: string, uid: string) {
  return fetchItemsWithFilter<WikiPage>(`${tenantId}_wiki`, [statusEquals('draft'), createdByEquals(uid)]);
}

export async function listByCategory(tenantId: string, category: string) {
  return fetchItemsWithFilter<WikiPage>(`${tenantId}_wiki`, [categoryContains(category)]);
}

export async function listByTag(tenantId: string, tag: string) {
  return fetchItemsWithFilter<WikiPage>(`${tenantId}_wiki`, [tagContains(tag)]);
}

export async function listUpdatedSince(tenantId: string, sinceTs: number) {
  return fetchItemsWithFilter<WikiPage>(`${tenantId}_wiki`, [updatedAtGte(sinceTs)]);
}

// -------------------------
// 🧱 CRUD wrappers
// -------------------------

/** Create (slug is the doc id). Stamps created/updated fields from userId. */
export async function createWikiPage(
  tenantId: string,
  slug: string,
  data: WikiPageCreate,
  userId: string,
): Promise<WikiPage> {
  // ensure slug is free
  const existing = await getWikiPage(tenantId, slug);
  if (existing) throw new Error(`Wiki page with slug "${slug}" already exists`);

  const { parentSlug, level } = deriveHierarchy(slug);
  const now = Date.now();

  const page: WikiPage = {
    ...data,
    slug,
    parentSlug,
    level,
    createdBy: userId,
    createdAt: now,
    updatedBy: userId,
    updatedAt: now,
  };

  await createItemWithId(wikiCollectionPath(tenantId), slug, page);
  return page;
}

/** Update by slug. Slug is immutable here; pass the existing slug in patch.slug. */
export async function updateWikiPage(tenantId: string, patch: WikiPageUpdate, userId: string): Promise<void> {
  if (!patch.slug) throw new Error("updateWikiPage: 'slug' is required on the patch");
  const current = await getWikiPage(tenantId, patch.slug);
  if (!current) throw new Error(`Wiki page "${patch.slug}" not found`);

  // Prevent accidental overwrite of immutables
  const {
    slug: _keepSlug,
    createdAt: _ignoreCreatedAt,
    createdBy: _ignoreCreatedBy,
    // parentSlug/level derived from slug; keep as-is unless you implement rename
    parentSlug: _ignoreParent,
    level: _ignoreLevel,
    ...mutable
  } = patch as any;

  await updateItem(wikiCollectionPath(tenantId), current.slug, {
    ...mutable,
    updatedBy: userId,
    updatedAt: Date.now(),
  });
}

/** Soft-delete (archive) helper. */
export async function softDeleteWikiPage(tenantId: string, slug: string, userId: string): Promise<void> {
  await updateItem(wikiCollectionPath(tenantId), slug, {
    status: 'archived',
    updatedBy: userId,
    updatedAt: Date.now(),
  } as Partial<WikiPage>);
}

/** Safe rename flow (copy -> adjust hierarchy -> delete old). */
export async function renameWikiPage(
  tenantId: string,
  oldSlug: string,
  newSlug: string,
  userId: string,
): Promise<void> {
  if (oldSlug === newSlug) return;

  const existing = await getWikiPage(tenantId, newSlug);
  if (existing) throw new Error(`Target slug "${newSlug}" already exists`);

  const page = await getWikiPage(tenantId, oldSlug);
  if (!page) throw new Error(`Source slug "${oldSlug}" not found`);

  const { parentSlug, level } = deriveHierarchy(newSlug);
  const now = Date.now();

  // create new doc with updated slug + hierarchy; keep created* fields
  await createItemWithId(wikiCollectionPath(tenantId), newSlug, {
    ...page,
    slug: newSlug,
    parentSlug,
    level,
    updatedBy: userId,
    updatedAt: now,
  });

  // delete old
  await deleteWikiPage(tenantId, oldSlug);
}

/**
 * Internal: get all descendant page doc refs using the slug convention.
 * Descendants are any pages whose slug starts with `${slug}-`
 */
async function getDescendantPageRefs(tenantId: string, slug: string): Promise<DocumentReference[]> {
  const colPath = wikiCollectionPath(tenantId);
  const colRef = collection(db, colPath);

  const childPrefix = `${slug}-`;
  // Prefix range query: [prefix, prefix + \uf8ff]
  const qDesc = query(colRef, where('slug', '>=', childPrefix), where('slug', '<=', childPrefix + '\uf8ff'));

  const snap = await getDocs(qDesc);
  return snap.docs.map((d) => doc(db, colPath, d.id));
}

/**
 * Optional helper: delete revisions subcollection for a page.
 * Safe to call even if you don't have revisions yet.
 */
async function deleteRevisionsForPage(pageRef: DocumentReference): Promise<number> {
  const revsRef = collection(pageRef, 'revisions');
  const revSnap = await getDocs(revsRef);
  if (revSnap.empty) return 0;

  let deleted = 0;
  // Batch in chunks of 500
  for (let i = 0; i < revSnap.docs.length; i += 500) {
    const batch = writeBatch(db);
    revSnap.docs.slice(i, i + 500).forEach((r) => batch.delete(r.ref));
    await batch.commit();
    deleted += Math.min(500, revSnap.docs.length - i);
  }
  return deleted;
}

/**
 * Cascade delete: deletes the page and all descendants (by slug prefix).
 * Returns counts so you can warn in UI if desired.
 */
export async function deleteWikiPageCascade(
  tenantId: string,
  slug: string,
  opts: { deleteRevisions?: boolean } = {},
): Promise<{ deletedPages: number; deletedRevisions: number }> {
  const colPath = wikiCollectionPath(tenantId);
  const parentRef = doc(db, colPath, slug);

  // Collect all targets: parent + descendants
  const descendants = await getDescendantPageRefs(tenantId, slug);
  const toDelete: DocumentReference[] = [parentRef, ...descendants];

  let deletedRevisions = 0;

  if (opts.deleteRevisions) {
    // Delete revisions for each page (parent + descendants)
    for (const pageRef of toDelete) {
      deletedRevisions += await deleteRevisionsForPage(pageRef);
    }
  }

  // Delete pages in batches of 500
  let deletedPages = 0;
  for (let i = 0; i < toDelete.length; i += 500) {
    const batch = writeBatch(db);
    toDelete.slice(i, i + 500).forEach((r) => batch.delete(r));
    await batch.commit();
    deletedPages += Math.min(500, toDelete.length - i);
  }

  return { deletedPages, deletedRevisions };
}

/**
 * Drop-in replacement for your existing delete.
 * Cascades to all subpages. Keeps the same signature (void).
 */
export async function deleteWikiPage(tenantId: string, slug: string): Promise<void> {
  await deleteWikiPageCascade(tenantId, slug); // ignore counts; UI can call cascade directly if it wants numbers
}

/**
 * Rename a page AND all descendants by slug prefix.
 * Example: old 'agp' -> new 'agp-docs' will move:
 *   'agp'                 -> 'agp-docs'
 *   'agp-excise'          -> 'agp-docs-excise'
 *   'agp-excise-rates'    -> 'agp-docs-excise-rates'
 */
export async function renameWikiPageCascade(
  tenantId: string,
  oldSlug: string,
  newSlug: string,
  userId: string,
  opts: { moveRevisions?: boolean } = {},
): Promise<{ movedPages: number; movedRevisions: number }> {
  if (oldSlug === newSlug) return { movedPages: 0, movedRevisions: 0 };

  const colPath = wikiCollectionPath(tenantId);
  const colRef = collection(db, colPath);

  // 1) Collect parent + all descendants under old prefix
  const childPrefix = `${oldSlug}-`;
  const qDesc = query(colRef, where('slug', '>=', childPrefix), where('slug', '<=', childPrefix + '\uf8ff'));
  const descSnap = await getDocs(qDesc);

  // Build full set (parent first)
  const parent = await getWikiPage(tenantId, oldSlug);
  if (!parent) throw new Error(`renameWikiPageCascade: source "${oldSlug}" not found`);

  const allDocs: Array<{ oldId: string; data: WikiPage }> = [
    { oldId: oldSlug, data: parent },
    ...descSnap.docs.map((d) => ({ oldId: d.id, data: d.data() as WikiPage })),
  ];

  // 2) Compute new slugs (prefix replace) and stamp hierarchy + audit fields
  const now = Date.now();
  const renameMap = allDocs.map(({ oldId, data }) => {
    const suffix = oldId === oldSlug ? '' : oldId.slice(oldSlug.length); // includes leading '-' for children
    const nextSlug = `${newSlug}${suffix}`;
    const { parentSlug, level } = deriveHierarchy(nextSlug);
    const nextData: WikiPage = omitUndefinedDeep({
      ...data,
      slug: nextSlug,
      parentSlug,
      level,
      updatedBy: userId,
      updatedAt: now,
      // keep createdAt/createdBy as-is
    });
    return { oldId, newId: nextSlug, nextData };
  });

  // 3) Optionally move revisions: copy under new path, then delete old
  let movedRevisions = 0;
  if (opts.moveRevisions) {
    for (const { oldId, newId } of renameMap) {
      const oldPageRef = doc(db, colPath, oldId);
      const newPageRef = doc(db, colPath, newId);

      // read all revisions under old
      const oldRevsRef = collection(oldPageRef, 'revisions');
      const oldRevsSnap = await getDocs(oldRevsRef);

      if (!oldRevsSnap.empty) {
        // copy in chunks
        for (let i = 0; i < oldRevsSnap.docs.length; i += 500) {
          const batch = writeBatch(db);
          oldRevsSnap.docs.slice(i, i + 500).forEach((r) => {
            const newRevRef = doc(collection(newPageRef, 'revisions'));
            batch.set(newRevRef, r.data());
          });
          await batch.commit();
        }
        movedRevisions += oldRevsSnap.size;

        // delete old revisions (chunks)
        for (let i = 0; i < oldRevsSnap.docs.length; i += 500) {
          const batch = writeBatch(db);
          oldRevsSnap.docs.slice(i, i + 500).forEach((r) => batch.delete(r.ref));
          await batch.commit();
        }
      }
    }
  }

  // 4) Write all new docs (chunks)
  for (let i = 0; i < renameMap.length; i += 500) {
    const batch = writeBatch(db);
    renameMap.slice(i, i + 500).forEach(({ newId, nextData }) => {
      batch.set(doc(db, colPath, newId), nextData);
    });
    await batch.commit();
  }

  // 5) Delete all old docs (chunks)
  for (let i = 0; i < renameMap.length; i += 500) {
    const batch = writeBatch(db);
    renameMap.slice(i, i + 500).forEach(({ oldId }) => {
      batch.delete(doc(db, colPath, oldId));
    });
    await batch.commit();
  }

  return { movedPages: renameMap.length, movedRevisions };
}

// ---------- Preview helpers (for UI confirms) ----------
export async function previewCascade(
  tenantId: string,
  slug: string,
): Promise<{ totalPages: number; pageIds: string[] }> {
  // parent + descendants
  const colPath = wikiCollectionPath(tenantId);
  const parentId = slug;

  const childPrefix = `${slug}-`;
  const childQ = query(
    collection(db, colPath),
    where('slug', '>=', childPrefix),
    where('slug', '<=', childPrefix + '\uf8ff'),
  );
  const childSnap = await getDocs(childQ);

  const pageIds = [parentId, ...childSnap.docs.map((d) => d.id)];
  return { totalPages: pageIds.length, pageIds };
}

// Convenience wrappers
export async function previewDeleteCascade(tenantId: string, slug: string) {
  return previewCascade(tenantId, slug);
}
export async function previewRenameCascade(tenantId: string, oldSlug: string, _newSlug: string) {
  // same set; rename moves the same subtree
  return previewCascade(tenantId, oldSlug);
}

// ---------- Internal link fixer (run AFTER rename) ----------
// Scans the renamed subtree and replaces internal links that still point to the old slug.
// Supports markdown links like /wiki/<slug>... and optional [[wikilinks]] style.
export async function fixInternalLinksAfterRename(
  tenantId: string,
  oldSlug: string,
  newSlug: string,
  userId: string,
): Promise<{ updated: number }> {
  const colPath = wikiCollectionPath(tenantId);
  const newPrefix = `${newSlug}`;
  const qSubtree = query(
    collection(db, colPath),
    where('slug', '>=', newPrefix),
    where('slug', '<=', newPrefix + '\uf8ff'),
  );
  const snap = await getDocs(qSubtree);
  if (snap.empty) return { updated: 0 };

  // Regexes:
  // 1) URL paths: /wiki/oldSlug[-...optional-suffix]
  const reUrl = new RegExp(`(/wiki/)${oldSlug}([-/\\w]*)`, 'g');
  // 2) Optional wikilinks: [[oldSlug(-suffix)?]]
  const reWiki = new RegExp(`\\[\\[${oldSlug}([\\-\\w]*)\\]\\]`, 'g');

  let updated = 0;

  // Batch in chunks
  for (let i = 0; i < snap.docs.length; i += 500) {
    const batch = writeBatch(db);

    snap.docs.slice(i, i + 500).forEach((d) => {
      const data = omitUndefinedDeep(d.data()) as WikiPage;
      console.log('Fixing internal links for:', data);
      const before = data.contentMd ?? '';
      let after = before.replace(reUrl, (_m, p1, suffix) => `${p1}${newSlug}${suffix}`);
      after = after.replace(reWiki, (_m, suffix) => `[[${newSlug}${suffix}]]`);

      if (after !== before) {
        batch.set(doc(db, colPath, d.id), {
          ...data,
          contentMd: after,
          updatedBy: userId,
          updatedAt: Date.now(),
        });
        updated++;
      }
    });

    await batch.commit();
  }

  return { updated };
}
